import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { usePermission } from '../../context/AuthContext';
import { formatDateTime } from '../../lib/formatDate';
import { useDebounce } from '../../hooks/useDebounce';
import {
  fetchOrgChart,
  buildTree, collectStatuses, ancestorIdsOf, pruneToMatches,
} from './orgChartApi';
import OrgChartToolbar from './OrgChartToolbar';
import OrgStatsStrip from './OrgStatsStrip';
import {
  SEATS_STATUS_FILTER, ACTING_STATUS_FILTER, OTHER_STATUS_FILTER,
  PEOPLE_STATUS_FILTER, NAMED_STATUSES,
} from './statusFilters';
import OrgTreeView from './OrgTreeView';
import OrgListView from './OrgListView';
import OrgPrintDialog from './OrgPrintDialog';
import OrgPrintView from './OrgPrintView';
import OrgPositionsPrintView from './OrgPositionsPrintView';
import useOrgChartPrint from './useOrgChartPrint';
import { scopeToDepartment, printCaption } from './orgChartExport';
import './orgChart.css';
import './orgChartPrint.css';

// org: null means "every available org selected" (the neutral, no-narrowing
// state used on load and after Reset). Once the reader unticks at least one
// company it becomes a Set of the still-selected org names.
const EMPTY_FILTERS = { func: 'all', org: null, status: 'all' };

// Default zoom for the tree view. 70% shows more of a wide tree on first
// paint than 100% did, at the cost of smaller text, which the zoom stepper
// still lets a reader recover.
const DEFAULT_ZOOM = 0.7;
const ZOOM_MIN = 0.4;
const ZOOM_MAX = 1.3;


/** Ids of every node with children, below the first level. Shared by the
 * default-on-load collapse and the toolbar's "Collapse all" action so the
 * two cannot drift. */
function idsBelowFirstLevel(roots) {
  const ids = new Set();
  const walk = (n, depth) => {
    if (n.children.length > 0 && depth >= 1) ids.add(n.id);
    n.children.forEach((c) => walk(c, depth + 1));
  };
  roots.forEach((r) => walk(r, 0));
  return ids;
}

export default function OrgChartPage() {
  // Full-view holders (admins, HR) get the all-companies option, mismatch
  // badges and unredacted legend labels, matching what the server returns.
  const isAdmin = usePermission('org_chart.view_all');
  const [searchParams, setSearchParams] = useSearchParams();

  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [view, setView] = useState('tree');
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [search, setSearch] = useState('');
  // Debounced so typing does not recompute `matches` (and every derived
  // count/expansion) on every keystroke.
  const debouncedSearch = useDebounce(search, 300);
  const [collapsed, setCollapsed] = useState(new Set());
  const [zoom, setZoom] = useState(DEFAULT_ZOOM);
  const [allCompanies, setAllCompanies] = useState(false);
  const [focusId, setFocusId] = useState(null);
  // Bumped to ask OrgTreeView to recentre its scroll position. A counter
  // rather than a boolean so a second click while nothing else changed still
  // fires the effect: OrgTreeView watches this value change, not its truthiness.
  const [centerSignal, setCenterSignal] = useState(0);
  // The .oc-viewport element, forwarded down by whichever view is mounted, so
  // the Fullscreen control can call requestFullscreen()/exitFullscreen() on
  // the actual scrollable chart container rather than some other wrapper.
  const viewportRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [controlsOpen, setControlsOpen] = useState(false);
  // PDF export state. `printScope` is the department a pending print is scoped
  // to ('all' for the whole current view), declared up here with the other
  // state because the `scopedNodes` memo below reads it. It is deliberately
  // NOT filters.func: that would also change what the reader sees on screen
  // after the print, whereas a print scope is transient.
  const [printScope, setPrintScope] = useState('all');
  // Which document a pending print renders: the full chart ('chart') or the
  // job-positions sheet ('positions'). Held beside printScope because the same
  // print run reads both.
  const [printMode, setPrintMode] = useState('chart');
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  // Guards every post-await state write below. A cleanup running between the
  // await in load() starting and it resolving cannot stop that call from
  // completing, so the resolved branch would otherwise call setPayload /
  // setError / setLoading on an unmounted component. Every completion path
  // below checks this ref first and bails out silently if the page is gone.
  const mountedRef = useRef(true);

  const load = useCallback(async (opts = {}) => {
    setLoading(true);
    setError(false);
    try {
      const data = await fetchOrgChart({ allCompanies: opts.allCompanies ?? allCompanies });
      if (!mountedRef.current) return;
      setPayload(data);
    } catch {
      if (!mountedRef.current) return;
      // Load failures stay inline per the client conventions, never a toast.
      setError(true);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [allCompanies]);

  useEffect(() => { load(); }, [load]);

  // Marks the page mounted/unmounted so a load() already in flight cannot set
  // state once this runs.
  //
  // The ref is re-armed on every mount, not just at useRef(true). StrictMode
  // double-invokes effects in development (mount, cleanup, remount), so a ref
  // that is only ever set false by the cleanup stays false after the remount
  // and every guarded state write below becomes a no-op, leaving the page
  // stuck on its loading state forever.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Organization is a SCOPE choice: unticking a company means "do not show me
  // that company" and its nodes are removed outright, before counting. That is
  // different from Function, Status and Search, which prune the built tree
  // (see `visibleRoots`) and keep the managers above a match, because a
  // non-matching manager there may be the only path to a node that does match.
  // Excluding a whole company cannot orphan anything inside the companies you
  // kept.
  //
  // A kept node whose manager was excluded is promoted to a root, so the
  // rendered set never references a node it does not contain. That mirrors what
  // the server's own company filter does for the configured scope.
  const nodes = useMemo(() => {
    const source = payload?.nodes || [];
    const selected = filters.org;
    if (selected === null) return source;
    const visible = source.filter((n) => selected.has(n.org));
    const visibleIds = new Set(visible.map((n) => n.id));
    return visible.map((n) => (
      n.managerId && !visibleIds.has(n.managerId) ? { ...n, managerId: null } : n
    ));
    // Keyed on `payload` rather than `allNodes`: the latter is a fresh array
    // literal every render, which would defeat the memo entirely.
  }, [payload, filters.org]);

  // The print scope narrows the rendered set to one department while a print is
  // pending, REMOVING everything else rather than dimming it (a page of mostly
  // faded cards is not a department chart). 'all' is the normal state and costs
  // nothing: scopeToDepartment returns the same array reference.
  const scopedNodes = useMemo(
    () => scopeToDepartment(nodes, printScope),
    [nodes, printScope]
  );

  // The admin's order for the second level, shipped with the payload so the
  // chart never paints in Odoo order and then reshuffles.
  const topOrder = useMemo(() => payload?.top_order || [], [payload]);

  const roots = useMemo(
    () => buildTree(scopedNodes, topOrder),
    [scopedNodes, topOrder]
  );

  // Collapse below the first level the first time a payload arrives, so the
  // initial paint of a large tree stays small. This must run exactly once per
  // payload (not on every render, and not every time `roots` is recomputed by
  // an unrelated state change), and it must not fight a user who has since
  // expanded or collapsed things by hand, so it is guarded by a ref rather
  // than depending on `collapsed` or being called unconditionally.
  //
  // This runs BEFORE the focus deep-link effect below, so a ?focus= link
  // still wins: the deep-link effect un-collapses the target's ancestors
  // after this default collapse has already applied.
  const defaultCollapseAppliedRef = useRef(false);
  useEffect(() => {
    if (defaultCollapseAppliedRef.current) return;
    if (roots.length === 0) return;
    defaultCollapseAppliedRef.current = true;
    setCollapsed(idsBelowFirstLevel(roots));
  }, [roots]);

  // Default the Organization selection to the PRIMARY company only, once per
  // payload. The server scope deliberately includes the other companies so their
  // data is fetched and their checkboxes are tickable on demand, but opening the
  // page on every company at once buries the main org chart in unrelated trees.
  //
  // "Primary" is the lowest Odoo company id in the payload rather than a
  // hardcoded name, since the ids are stable and the names are not ours to
  // depend on. Runs once per payload so it cannot fight a reader who has since
  // ticked more companies, and skips entirely when there is only one company
  // (leaving the cheap `null` neutral state in place).
  const defaultOrgAppliedRef = useRef(false);
  useEffect(() => {
    if (defaultOrgAppliedRef.current) return;
    const companies = payload?.companies || [];
    if (companies.length < 2) return;
    defaultOrgAppliedRef.current = true;
    const primary = [...companies].sort((a, b) => a.id - b.id)[0];
    if (!primary?.name) return;
    setFilters((prev) => ({ ...prev, org: new Set([primary.name]) }));
  }, [payload]);

  // Focus deep link. Read once, expand the target's ancestors, then strip the
  // param so a reload or a shared URL does not keep re-focusing.
  useEffect(() => {
    const wanted = searchParams.get('focus');
    if (!wanted || nodes.length === 0) return;
    if (!nodes.some((n) => n.id === wanted)) {
      // Not in the payload (filtered company, or no longer in Odoo). Load
      // normally rather than showing an error for a stale link.
      setSearchParams({}, { replace: true });
      return;
    }
    const ancestors = new Set(ancestorIdsOf(nodes, wanted));
    setCollapsed((prev) => new Set([...prev].filter((id) => !ancestors.has(id))));
    setFocusId(wanted);
    setSearchParams({}, { replace: true });
    const t = setTimeout(() => setFocusId(null), 2500);
    return () => clearTimeout(t);
  }, [nodes, searchParams, setSearchParams]);

  useEffect(() => {
    if (!focusId || typeof document === 'undefined') return;
    const el = document.querySelector('.oc-focus');
    if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [focusId]);

  const functions = useMemo(
    () => [...new Set(nodes.map((n) => n.func).filter(Boolean))].sort(),
    [nodes]
  );
  // From the FULL payload, not the org-filtered set: unticking a company must not
  // remove its own checkbox, or it could never be ticked back on.
  // Keyed on `payload`, not `allNodes`: the latter is a fresh array literal on
  // every render, which would defeat the memo.
  const orgs = useMemo(
    () => [...new Set((payload?.nodes || []).map((n) => n.org).filter(Boolean))].sort(),
    [payload]
  );
  const statuses = useMemo(() => collectStatuses(nodes), [nodes]);

  const trimmedSearch = debouncedSearch.trim().toLowerCase();
  // filters.org is null (neutral, every org selected) or a Set of the
  // selected org names. Either way, "narrowed" only when it is an actual
  // subset, so a Set covering every available org still counts as no filter.
  const orgNarrowed = filters.org !== null && filters.org.size < orgs.length;
  const anyFilter = filters.func !== 'all' || orgNarrowed
    || filters.status !== 'all' || trimmedSearch !== '';
  // Composes with the Function/Organization/Status filters (AND, not OR), so
  // a search narrows within the current filter selection rather than fighting
  // it. Matching against `name` OR `title`, case-insensitively, covers both
  // "who is this person" and "who holds this job title" searches.
  //
  // The org check stays a cheap `=== null` skip in the neutral case (every
  // org ticked) rather than doing a Set.has() per node on the fast path.
  // No org test here: unticked companies are already removed from `nodes` above,
  // so the remaining filters only decide which of those nodes are SHOWN.
  // The status test has three shapes: 'all' (no status filter), the seats
  // sentinel (kind === 'seat', which is not a status value at all), or a real
  // status string compared directly. This is the SAME `filters.status` the
  // Status <select> reads and writes, so a stat tile click and a dropdown
  // pick can never disagree about what is currently selected.
  const matchesStatus = useCallback((n) => {
    if (filters.status === 'all') return true;
    if (filters.status === SEATS_STATUS_FILTER) return n.kind === 'seat';
    if (filters.status === PEOPLE_STATUS_FILTER) return n.kind !== 'seat';
    // Acting cover lives on seat cards only, so this is a seat test plus the
    // presence of a name, not a status comparison.
    if (filters.status === ACTING_STATUS_FILTER) return n.kind === 'seat' && !!n.acting_name;
    // Everything filled that no named tile claims. Seats are excluded: they are
    // counted by Open, not by a status.
    if (filters.status === OTHER_STATUS_FILTER) {
      return n.kind !== 'seat' && !NAMED_STATUSES.includes(n.status);
    }
    return n.status === filters.status;
  }, [filters.status]);

  // Everything EXCEPT the status filter. This is the scope the reader has
  // chosen to look at — a function, a company, a search — as opposed to which
  // slice of that scope a status tile is currently selecting.
  const matchesScope = useCallback((n) => (
    (filters.func === 'all' || n.func === filters.func)
    && (trimmedSearch === ''
      || String(n.name || '').toLowerCase().includes(trimmedSearch)
      || String(n.title || '').toLowerCase().includes(trimmedSearch))
  ), [filters.func, trimmedSearch]);

  const matches = useCallback((n) => (
    matchesScope(n) && matchesStatus(n)
  ), [matchesScope, matchesStatus]);

  const matchedCount = useMemo(() => nodes.filter(matches).length, [nodes, matches]);

  // Counters come from the VISIBLE node set, never a server total. A manager's
  // payload is already redacted, so deriving here (from `nodes`, which is
  // already the redacted payload) is what stops the tiles from leaking a count
  // of what redaction hid for that manager's departments. That property holds
  // for the total below too: it is still derived from `nodes`, never fetched.
  //
  // EVERY counter — the total and each tile — is scoped by `matchesScope`,
  // which is every filter EXCEPT status. The status tiles are mutually
  // exclusive alternatives, not cumulative narrowings: a reader can only be in
  // one at a time, so what each one counts does not depend on which is
  // currently picked. Scoping them by the status filter as well meant choosing
  // Probation zeroed Active, On Notice and PIP, so the strip stopped being a
  // breakdown at all and the reader lost the very numbers they were choosing
  // between. It also kept the total honest as a denominator: picking
  // "On Notice" now reads 21 of 198, not 21 of 21.
  //
  // Function, Organization and search DO still scope every counter, because
  // those change WHICH org is being counted rather than which slice of it is
  // selected.
  const counts = useMemo(() => {
    let active = 0; let probation = 0; let onNotice = 0; let pip = 0; let seats = 0;
    let acting = 0; let other = 0;
    let total = 0;
    for (const n of nodes) {
      if (!matchesScope(n)) continue;
      total += 1;
      if (n.kind === 'seat') {
        seats += 1;
        // Counted as a SUBSET of open seats, not alongside them: an acting-covered
        // position is still vacant. Adding it as a separate category would make
        // the tiles sum to more than the positions total and imply the acting
        // person occupies headcount they do not.
        if (n.acting_name) acting += 1;
        continue;
      }
      if (n.status === 'Active') active += 1;
      else if (n.status === 'Probation') probation += 1;
      else if (n.status === 'On Notice') onNotice += 1;
      else if (n.status === 'PIP') pip += 1;
      // Every other filled position: leave, unpaid vacation, an unmapped or
      // absent status. Counted so the breakdown sums to the total rather than
      // quietly losing people the four named tiles do not describe.
      else other += 1;
    }
    // Employees is every filled card: one per person, so this is headcount
    // rather than positions. The status tiles break it down and sum to it.
    const employees = total - seats;
    return { total, employees, active, probation, onNotice, pip, seats, acting, other };
  }, [nodes, matchesScope]);

  // Function, Status and Search SHOW their matches: a branch holding no match
  // is removed, while the managers above a match stay (dimmed, since they fail
  // `matches`) so each result keeps its reporting line. Organization is not in
  // this test because it already removes nodes upstream, in `nodes`.
  const narrowing = filters.func !== 'all' || filters.status !== 'all' || trimmedSearch !== '';
  const visibleRoots = useMemo(
    () => (narrowing ? pruneToMatches(roots, matches) : roots),
    [narrowing, roots, matches]
  );

  // A match can still sit inside a branch collapsed by the default
  // collapse-below-first-level behaviour, where the reader would see its
  // manager and nothing else. So any active filter auto-expands the ancestors
  // of every match. This reuses ancestorIdsOf, the same helper the ?focus=
  // deep link uses, so the two expansion sources cannot drift or fight each
  // other: both simply remove ids from `collapsed`, and either can run without
  // undoing the other's expansion.
  useEffect(() => {
    if (!narrowing || nodes.length === 0) return;
    const toExpand = new Set();
    for (const n of nodes) {
      if (!matches(n)) continue;
      for (const id of ancestorIdsOf(nodes, n.id)) toExpand.add(id);
    }
    if (toExpand.size === 0) return;
    setCollapsed((prev) => {
      if (![...toExpand].some((id) => prev.has(id))) return prev;
      return new Set([...prev].filter((id) => !toExpand.has(id)));
    });
  }, [narrowing, nodes, matches]);

  // Pruning plus auto-expand is not enough on a wide tree: the match can
  // still land outside the viewport with no visual cue to scroll toward it.
  // Reuses the SAME mechanism as the ?focus= deep link (focusId, plus the
  // .oc-focus scrollIntoView effect just above) rather than a second
  // scrolling path, so the first match also gets the existing highlight ring
  // for free. Only fires while a search term is active and it actually
  // matches something; an empty search or zero matches leaves focusId alone.
  useEffect(() => {
    if (trimmedSearch === '' || nodes.length === 0) return;
    const first = nodes.find((n) => matches(n));
    if (!first) return;
    setFocusId(first.id);
    const t = setTimeout(() => setFocusId(null), 2500);
    return () => clearTimeout(t);
  }, [trimmedSearch, nodes, matches]);

  const toggle = useCallback((id) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const collapseAll = useCallback(() => {
    setCollapsed(idsBelowFirstLevel(roots));
  }, [roots]);

  // Reset returns to the page's OPENING state, which for Organization means the
  // primary company only, not every company ticked. Restoring "all" here would
  // make Reset produce a view the reader never started from.
  const reset = useCallback(() => {
    const companies = payload?.companies || [];
    const primary = companies.length >= 2
      ? [...companies].sort((a, b) => a.id - b.id)[0]
      : null;
    setFilters({
      ...EMPTY_FILTERS,
      org: primary?.name ? new Set([primary.name]) : null,
    });
    setSearch('');
    setCollapsed(new Set());
    setZoom(DEFAULT_ZOOM);
  }, [payload]);

  const onFilter = useCallback((key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  // --- PDF export ---------------------------------------------------------

  // What a print run overrides, and how to put it back.
  //
  // Far less than it used to be. The printed document is its own component
  // (OrgPrintView, sections rather than a tree), rendered off-screen from the
  // same `roots`, so a print no longer has to reshape the interactive view at
  // all: no view swap, no zoom reset, no expanding collapsed branches. Only the
  // department scope has to change, because that changes which nodes exist.
  const printSnapshot = useCallback(() => ({ search, controlsOpen }), [search, controlsOpen]);

  const printApply = useCallback((override) => {
    setPrintScope(override.dept);
    setPrintMode(override.mode || 'chart');
    // A search term is a live, transient cue on screen; in a PDF it would cut
    // the document down with no explanation, so the print always goes out
    // unfiltered by search. Function and Status filters DO carry into the
    // print, since `visibleRoots` feeds both documents.
    setSearch('');
    setControlsOpen(false);
  }, []);

  const printRestore = useCallback((saved) => {
    setPrintScope('all');
    setPrintMode('chart');
    setSearch(saved.search);
    setControlsOpen(saved.controlsOpen);
  }, []);

  const { printing, start: startPrint } = useOrgChartPrint({
    snapshot: printSnapshot, apply: printApply, restore: printRestore,
  });

  // No fit decision to make any more: the printed document is one section per
  // manager with wrapping card rows, so it fits a landscape page whatever the
  // org's shape and nothing has to be measured or degraded to the list view.
  const onPrint = useCallback((dept, opts = {}) => {
    setPrintDialogOpen(false);
    startPrint({ dept, mode: opts.mode || 'chart' });
  }, [startPrint]);

  // A stat tile click and the Status <select> write the exact same piece of
  // state, so the two views can never disagree about what is selected.
  // Clicking the currently-active tile clears back to 'all' (toggle off);
  // clicking Total positions always clears, since the total is not a status.
  const onStatusTile = useCallback((value) => {
    setFilters((prev) => ({ ...prev, status: prev.status === value ? 'all' : value }));
  }, []);

  const onCenter = useCallback(() => {
    setCenterSignal((s) => s + 1);
  }, []);

  // Fullscreen availability is feature-detected once: some browsers (older
  // Safari, some embedded webviews) never define requestFullscreen, and a
  // button that is shown but always rejects is worse than no button.
  const [fullscreenSupported, setFullscreenSupported] = useState(
    typeof document !== 'undefined'
    && typeof document.documentElement.requestFullscreen === 'function'
  );

  const onFullscreen = useCallback(() => {
    const vp = viewportRef.current;
    if (!vp) return;
    if (document.fullscreenElement) {
      // requestFullscreen/exitFullscreen both return a Promise that can
      // reject (permissions policy, no user gesture, etc). Swallow the
      // rejection rather than crash the page over a browser that declined.
      document.exitFullscreen().catch(() => {});
    } else if (typeof vp.requestFullscreen === 'function') {
      vp.requestFullscreen().catch(() => {});
    } else {
      setFullscreenSupported(false);
    }
  }, []);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const onChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  // Toggling one company's checkbox. Starts from every available org (not
  // from the current Set) when filters.org is still null, so the first
  // untick narrows from "all" rather than from an empty selection. Collapses
  // back to null once every org ends up checked again, so the neutral state
  // stays the cheap `=== null` skip in `matches` instead of drifting into an
  // always-true Set comparison.
  const onOrgToggle = useCallback((orgName) => {
    setFilters((prev) => {
      const current = prev.org === null ? new Set(orgs) : new Set(prev.org);
      if (current.has(orgName)) current.delete(orgName); else current.add(orgName);
      const next = current.size >= orgs.length ? null : current;
      return { ...prev, org: next };
    });
  }, [orgs]);

  // Only flip the state. `load` closes over `allCompanies`, so changing it gives
  // `load` a new identity and the mount effect below re-runs it with the new
  // scope. Calling load() here as well fired two requests per toggle.
  const onAllCompanies = useCallback((next) => {
    setAllCompanies(next);
  }, []);

  if (loading && !payload) {
    return (
      <div className="main-content-inner">
        <div className="oc-empty" role="status" aria-live="polite">Loading the org chart...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="main-content-inner">
        <div className="page-head">
          <p className="page-eyebrow">Organization</p>
          <h1 className="page-title">Org Chart</h1>
        </div>
        <div className="oc-error">
          <p>Could not load the org chart.</p>
          <button type="button" className="btn" onClick={() => load()}>Retry</button>
        </div>
      </div>
    );
  }

  // Fullscreen strips every surrounding chrome: no toolbar, no filter panel, no
  // stat strip, no page header. The toolbar's own Fullscreen/Exit button lives
  // outside .oc-viewport, so it is not part of what the browser renders once
  // .oc-viewport is the fullscreen element, which leaves the reader with no
  // way back except Escape. This overlay is the minimal affordance that
  // replaces it: an Exit control, plus zoom and Center in tree view, since a
  // chart the reader cannot pan or zoom is not very useful at full screen
  // size. It renders as the last child of .oc-viewport (passed down as
  // `fullscreenOverlay`) so it is a descendant of the fullscreen element and
  // stays visible, and only while isFullscreen is true so it never shows in
  // the normal page layout.
  const fullscreenOverlay = isFullscreen ? (
    <div className="oc-fs-overlay">
      {view === 'tree' && (
        <div className="oc-zoom oc-fs-zoom">
          <button type="button" onClick={() => setZoom((z) => (
            Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round((z - 0.1) * 10) / 10))
          ))} aria-label="Zoom out">-</button>
          <span>{Math.round(zoom * 100)}%</span>
          <button type="button" onClick={() => setZoom((z) => (
            Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round((z + 0.1) * 10) / 10))
          ))} aria-label="Zoom in">+</button>
        </div>
      )}
      {view === 'tree' && (
        <button type="button" className="btn btn-secondary oc-fs-btn" onClick={onCenter}>
          Center
        </button>
      )}
      <button
        type="button"
        className="btn btn-secondary oc-fs-btn oc-fs-exit"
        onClick={onFullscreen}
      >
        Exit fullscreen view
      </button>
    </div>
  ) : null;

  return (
    <div className="main-content-inner">
      {/* The printable document: one section per manager, rendered off-screen and
          revealed only by the print stylesheet. Built from the same `roots` as
          the interactive view, so the two can never disagree about the
          hierarchy, but laid out to paginate instead of to pan.

          Mounted ONLY while a print is pending. It renders a second card for
          every node, so keeping it mounted permanently would duplicate hundreds
          of nodes in the accessibility tree and in any query over the page, for
          a document nobody is looking at until they ask to print. */}
      {printing && printMode === 'positions' && (
        <OrgPositionsPrintView
          roots={visibleRoots}
          title="Job Positions"
          caption={printCaption({
            dept: printScope,
            orgs: filters.org === null ? orgs : [...filters.org],
            synced: payload?.synced_at ? formatDateTime(payload.synced_at) : '',
          })}
          printedOn={formatDateTime(new Date().toISOString())}
        />
      )}

      {printing && printMode === 'chart' && (
        <OrgPrintView
          roots={visibleRoots}
          isAdmin={isAdmin}
          title="Organization Chart"
          caption={printCaption({
            dept: printScope,
            orgs: filters.org === null ? orgs : [...filters.org],
            synced: payload?.synced_at ? formatDateTime(payload.synced_at) : '',
          })}
          // Stamped when the print actually starts, not at module load, so a tab
          // left open overnight does not print yesterday's date in its footer.
          printedOn={formatDateTime(new Date().toISOString())}
        />
      )}

      {/* Suppressed while a print is in flight. The print stylesheet already
          hides the dialog from the PDF, but leaving it mounted lets a second
          click land during the frames between applying the override and the
          dialog opening, which would snapshot the already-overridden state as
          if it were the reader's own view and restore the wrong thing after. */}
      <OrgPrintDialog
        open={printDialogOpen && !printing}
        onPrint={onPrint}
        onClose={() => setPrintDialogOpen(false)}
      />

      {/* Local wrapper, not a change to the shared .page-head styling: other
          pages depend on that class rendering full width on its own. This
          puts the title band and the stat strip side by side within one row
          without touching page-head/page-eyebrow/page-title/page-subtitle. */}
      <div className="oc-head-row">
        <div className="page-head">
          <p className="page-eyebrow">Organization</p>
          <h1 className="page-title">Org Chart</h1>
          <p className="page-subtitle">Reporting structure from Odoo HR</p>
        </div>

        <OrgStatsStrip
          counts={counts} status={filters.status} isAdmin={isAdmin}
          onSelect={onStatusTile}
        />
      </div>

      <details
        className="oc-controls-panel"
        open={controlsOpen}
        onToggle={(e) => setControlsOpen(e.target.open)}
      >
        <summary className="oc-controls-summary">
          Filters and view
          {anyFilter && <span className="oc-controls-badge">Filters active</span>}
        </summary>

        <OrgChartToolbar
          view={view} onView={setView}
          functions={functions} org={orgs} statuses={statuses}
          filters={filters} onFilter={onFilter} onOrgToggle={onOrgToggle}
          search={search} onSearch={setSearch}
          matchedCount={matchedCount} anyFilter={anyFilter}
          zoom={zoom}
          onZoom={(d) => setZoom((z) => (
            Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round((z + d) * 10) / 10))
          ))}
          onExpandAll={() => setCollapsed(new Set())}
          onCollapseAll={collapseAll}
          onReset={reset}
          onCenter={onCenter}
          freshness={payload?.synced_at ? formatDateTime(payload.synced_at) : ''}
          isAdmin={isAdmin}
          allCompanies={allCompanies}
          onAllCompanies={onAllCompanies}
          fullscreenSupported={fullscreenSupported}
          isFullscreen={isFullscreen}
          onFullscreen={onFullscreen}
          onExport={() => setPrintDialogOpen(true)}
        />
      </details>

      {nodes.length === 0 ? (
        <div className="oc-empty">
          <p>No org chart data yet. It populates on the nightly sync.</p>
        </div>
      ) : visibleRoots.length === 0 ? (
        <div className="oc-empty">
          <p>No one matches these filters.</p>
        </div>
      ) : view === 'tree' ? (
        <OrgTreeView
          roots={visibleRoots} collapsed={collapsed} onToggle={toggle}
          matches={matches} focusId={focusId} isAdmin={isAdmin} zoom={zoom}
          centerSignal={centerSignal} viewportRef={viewportRef}
          fullscreenOverlay={fullscreenOverlay}
        />
      ) : (
        <OrgListView
          roots={visibleRoots} collapsed={collapsed} onToggle={toggle}
          matches={matches} focusId={focusId} isAdmin={isAdmin}
          viewportRef={viewportRef}
          fullscreenOverlay={fullscreenOverlay}
        />
      )}
    </div>
  );
}
